// src/pages/ventes/BonsLivraison.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Card, Table, Row, Col, Form, Button, Badge,
    Spinner, Alert, Tooltip, OverlayTrigger, Modal, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function BonsLivraison() {
    const navigate = useNavigate();
    const [bonsLivraison, setBonsLivraison] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [sortConfig, setSortConfig] = useState({ key: 'date_livraison', direction: 'desc' });
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    const [taxes, setTaxes] = useState({
        tva_taux: 18, css_taux: 1, tva_active: true, css_active: true
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmVariant, setConfirmVariant] = useState('primary');
    const [isProcessing, setIsProcessing] = useState(false);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });
    const showSuccess = (msg) => showToast('success', 'Succès', msg);
    const showError   = (msg) => showToast('error', 'Erreur', msg);
    const showInfo    = (msg) => showToast('info', 'Information', msg);

    useEffect(() => {
        fetchBonsLivraison();
        fetchStats();
        fetchSocieteInfos();
        fetchTaxes();
        loadLogoBase64();
    }, [filtreStatut]);

    /* ============================================================
       FETCH (logique conservée)
       ============================================================ */
    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setSocieteInfos(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    setLogoBase64(canvas.toDataURL('image/jpeg'));
                    resolve();
                };
                img.onerror = () => { setLogoBase64(''); resolve(); };
                img.src = logo;
            } catch (error) {
                setLogoBase64('');
                resolve();
            }
        });
    };

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setTaxes(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const openConfirmModal = (id, title, message, variant = 'primary', action) => {
        setConfirmId(id);
        setConfirmTitle(title);
        setConfirmMessage(message);
        setConfirmVariant(variant);
        setConfirmAction(() => action);
        setShowConfirmModal(true);
    };

    const fetchBonsLivraison = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/bons_livraison.php`;
            if (filtreStatut !== 'tous') url += `?statut=${filtreStatut}`;
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setBonsLivraison(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/bons_livraison.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const fetchBonLivraisonDetails = async (id) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement détails");
            const result = await response.json();
            return result.data;
        } catch (err) {
            showError("Impossible de charger les détails du bon de livraison");
            return null;
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getStatutConfig = (statut) => {
        const configs = {
            brouillon: { gradient: 'linear-gradient(135deg, #64748b, #475569)', label: 'Brouillon', icon: 'ri-draft-line' },
            prepare:   { gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', label: 'Préparé',   icon: 'ri-stack-line' },
            livre:     { gradient: 'linear-gradient(135deg, #10b981, #059669)', label: 'Livré',     icon: 'ri-check-double-line' },
            annule:    { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Annulé',    icon: 'ri-close-circle-line' }
        };
        return configs[statut] || configs.brouillon;
    };

    const getStatutBadge = (statut) => {
        const config = getStatutConfig(statut);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const estEnRetard = (dateLivraison) => new Date(dateLivraison) < new Date();

    const filteredBons = useMemo(() => {
        return bonsLivraison
            .filter(b =>
                b.numero_bl?.toLowerCase().includes(filterText.toLowerCase()) ||
                b.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                b.code_client?.toLowerCase().includes(filterText.toLowerCase()) ||
                b.destinataire?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_livraison') {
                    return sortConfig.direction === 'asc'
                        ? new Date(a.date_livraison) - new Date(b.date_livraison)
                        : new Date(b.date_livraison) - new Date(a.date_livraison);
                }
                if (sortConfig.key === 'numero_bl') {
                    return sortConfig.direction === 'asc'
                        ? (a.numero_bl || '').localeCompare(b.numero_bl || '')
                        : (b.numero_bl || '').localeCompare(a.numero_bl || '');
                }
                return 0;
            });
    }, [bonsLivraison, filterText, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredBons.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredBons.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }
        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th onClick={() => handleSort(field)} className="sortable-th-charte">
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    /* ============================================================
       ACTIONS (logique conservée)
       ============================================================ */
    const handleMarquerLivre = async (id) => {
        openConfirmModal(
            id,
            "Confirmer la livraison",
            "Êtes-vous sûr de vouloir marquer ce bon de livraison comme <strong>livré</strong> ?",
            "success",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(`${API_BASE_URL}/bons_livraison.php`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ action: 'marquer_livre', id: confirmId })
                    });
                    const result = await response.json();
                    if (result.success) {
                        setShowConfirmModal(false);
                        showSuccess("Bon de livraison marqué comme livré");
                        fetchBonsLivraison();
                        fetchStats();
                    } else {
                        showError(result.message || "Erreur lors de la mise à jour");
                    }
                } catch (err) {
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const handlePreparer = async (id) => {
        openConfirmModal(
            id,
            "Confirmer la préparation",
            "Êtes-vous sûr de vouloir préparer ce bon de livraison ? Les produits seront déduits du stock.",
            "primary",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const userStr = localStorage.getItem("user");
                    const user = userStr ? JSON.parse(userStr) : null;

                    const getResponse = await fetch(
                        `${API_BASE_URL}/bons_livraison.php?id=${confirmId}`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    );
                    if (!getResponse.ok) throw new Error("Erreur chargement du BL");
                    const getResult = await getResponse.json();
                    const blData = getResult.data;

                    const updateResponse = await fetch(
                        `${API_BASE_URL}/bons_livraison.php?id=${confirmId}`,
                        {
                            method: 'PUT',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ ...blData, statut: 'prepare', id_utilisateur: user?.id || user?.id_utilisateur || 1 })
                        }
                    );
                    const updateResult = await updateResponse.json();
                    if (updateResult.success) {
                        setShowConfirmModal(false);
                        showSuccess("Bon de livraison marqué comme préparé");
                        fetchBonsLivraison();
                        fetchStats();
                    } else {
                        showError(updateResult.message || "Erreur lors de la mise à jour");
                    }
                } catch (err) {
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const handleAnnuler = async (id) => {
        openConfirmModal(
            id,
            "Confirmer l'annulation",
            "Êtes-vous sûr de vouloir annuler ce bon de livraison ? Cette action est irréversible.",
            "danger",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const getResponse = await fetch(
                        `${API_BASE_URL}/bons_livraison.php?id=${confirmId}`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    );
                    if (!getResponse.ok) throw new Error("Erreur chargement du BL");
                    const getResult = await getResponse.json();
                    const blData = getResult.data;

                    const updateResponse = await fetch(
                        `${API_BASE_URL}/bons_livraison.php?id=${confirmId}`,
                        {
                            method: 'PUT',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ ...blData, statut: 'annule' })
                        }
                    );
                    const updateResult = await updateResponse.json();
                    if (updateResult.success) {
                        setShowConfirmModal(false);
                        showSuccess("Bon de livraison annulé");
                        fetchBonsLivraison();
                        fetchStats();
                    } else {
                        showError(updateResult.message || "Erreur lors de l'annulation");
                    }
                } catch (err) {
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const company = {
        name: societeInfos?.nom || 'GEOTECH',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.geotech.com',
        rc: societeInfos?.rc || 'RC123456',
        nif: societeInfos?.nif || 'NIF123456',
        patente: societeInfos?.patente || 'PAT123456'
    };

    /* ============================================================
       IMPRESSION PROFESSIONNELLE — Design bordereau
       ============================================================ */
    const genererHTMLImpression = (bl) => {
        if (!bl) return '';

        const statutConfig = {
            brouillon: { class: 'draft', label: 'BROUILLON' },
            prepare:   { class: 'prepared', label: 'PRÉPARÉ' },
            livre:     { class: 'delivered', label: 'LIVRÉ' },
            annule:    { class: 'cancelled', label: 'ANNULÉ' }
        };
        const statut = statutConfig[bl.statut] || statutConfig.brouillon;
        const totalProduits = bl?.lignes?.reduce((acc, l) => acc + (parseInt(l.quantite) || 0), 0) || 0;
        const logoSrc = logoBase64;
        const logoText = company.name.substring(0, 2).toUpperCase();

        return `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <title>Bon de livraison ${bl.numero_bl || ''}</title>
                <style>
                    @page { size: A4; margin: 12mm 10mm; }
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif; background: white; color: #1e293b; font-size: 10.5px; line-height: 1.5; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .invoice { max-width: 210mm; margin: 0 auto; background: white; position: relative; }

                    .top-band { display: flex; justify-content: space-between; align-items: stretch; border-radius: 8px 8px 0 0; overflow: hidden; background: linear-gradient(135deg, #1a3c5e 0%, #295AA7 100%); color: white; }
                    .top-band-left { padding: 22px 26px; display: flex; align-items: center; gap: 18px; flex: 1; }
                    .logo-container { width: 70px; height: 70px; background: white; border-radius: 8px; display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
                    .logo-container img { max-width: 100%; max-height: 100%; object-fit: contain; }
                    .logo-fallback { font-size: 24px; font-weight: 800; color: #1a3c5e; letter-spacing: 1px; }
                    .company-header h1 { font-size: 22px; font-weight: 800; letter-spacing: 2px; margin-bottom: 4px; text-transform: uppercase; }
                    .company-header .tagline { font-size: 9px; letter-spacing: 3px; opacity: 0.75; text-transform: uppercase; font-weight: 300; }
                    .top-band-right { padding: 22px 26px; text-align: right; display: flex; flex-direction: column; justify-content: center; min-width: 220px; background: rgba(0,0,0,0.12); border-left: 1px solid rgba(255,255,255,0.15); }
                    .doc-type { font-size: 22px; font-weight: 800; letter-spacing: 3px; margin-bottom: 6px; text-transform: uppercase; }
                    .doc-number { font-size: 11px; font-family: 'Courier New', monospace; font-weight: 600; letter-spacing: 1px; margin-bottom: 8px; opacity: 0.95; }
                    .doc-date { font-size: 9px; opacity: 0.75; letter-spacing: 1px; }

                    .status-strip { display: flex; justify-content: center; align-items: center; padding: 8px 20px; font-size: 10px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; border-bottom: 3px solid; }
                    .status-delivered { background: #ecfdf5; color: #065f46; border-color: #10b981; }
                    .status-prepared  { background: #eff6ff; color: #1e40af; border-color: #3b82f6; }
                    .status-draft     { background: #f1f5f9; color: #475569; border-color: #94a3b8; }
                    .status-cancelled { background: #fef2f2; color: #991b1b; border-color: #ef4444; }

                    .parties { display: flex; gap: 14px; padding: 20px 0; border-bottom: 1px solid #e2e8f0; }
                    .party { flex: 1; padding: 14px 16px; background: #f8fafc; border-radius: 8px; border-left: 3px solid #295AA7; }
                    .party-client { border-left-color: #21ACCD; }
                    .party-label { font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #295AA7; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
                    .party-client .party-label { color: #21ACCD; }
                    .party-label::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
                    .party-name { font-size: 13px; font-weight: 700; color: #0f172a; margin-bottom: 6px; }
                    .party-details { font-size: 9.5px; color: #475569; line-height: 1.7; }
                    .party-details .label { display: inline-block; min-width: 70px; color: #94a3b8; font-weight: 500; }

                    .invoice-meta { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 0; margin-bottom: 8px; }
                    .meta-card { min-width: 150px; padding: 8px 14px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; text-align: center; }
                    .meta-card .meta-label { font-size: 8px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: #94a3b8; margin-bottom: 3px; }
                    .meta-card .meta-value { font-size: 11px; font-weight: 700; color: #1e293b; }
                    .meta-card.highlight { background: linear-gradient(135deg, #eff6ff, #dbeafe); border-color: #3b82f6; }
                    .meta-card.highlight .meta-label { color: #1e40af; }
                    .meta-card.highlight .meta-value { color: #1e3a8a; }

                    .notes-box { padding: 12px 16px; background: #fffbeb; border-left: 3px solid #f59e0b; border-radius: 6px; font-size: 9.5px; color: #78350f; margin-bottom: 16px; }
                    .notes-box strong { color: #92400e; }

                    .table-wrapper { border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0; margin-bottom: 16px; }
                    table.lines { width: 100%; border-collapse: collapse; }
                    table.lines thead { background: linear-gradient(135deg, #1a3c5e, #295AA7); color: white; }
                    table.lines thead th { padding: 10px 12px; font-size: 9px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; text-align: left; }
                    table.lines thead th.center { text-align: center; }
                    table.lines tbody td { padding: 10px 12px; font-size: 10px; color: #334155; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
                    table.lines tbody tr:nth-child(even) { background: #fafbfc; }
                    table.lines tbody tr:last-child td { border-bottom: none; }
                    table.lines td.center { text-align: center; }
                    table.lines td.ref { font-family: 'Courier New', monospace; font-size: 9px; color: #64748b; }
                    table.lines td.designation { font-weight: 600; color: #0f172a; }
                    table.lines td.quantity { text-align: center; font-weight: 700; color: #1a3c5e; }

                    .totals-section { display: flex; justify-content: flex-end; margin-bottom: 20px; }
                    .totals-box { width: 300px; flex-shrink: 0; border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0; }
                    .totals-box .row { display: flex; justify-content: space-between; align-items: center; padding: 8px 14px; font-size: 10px; border-bottom: 1px solid #f1f5f9; }
                    .totals-box .row:last-child { border-bottom: none; }
                    .totals-box .row .lbl { color: #64748b; }
                    .totals-box .row .val { font-weight: 600; color: #334155; }
                    .totals-box .row.total { background: linear-gradient(135deg, #1a3c5e, #295AA7); color: white; padding: 12px 14px; }
                    .totals-box .row.total .lbl { color: white; font-weight: 700; letter-spacing: 1.5px; font-size: 10px; text-transform: uppercase; }
                    .totals-box .row.total .val { color: white; font-size: 15px; font-weight: 800; }

                    .signatures { display: flex; justify-content: space-between; gap: 20px; margin-top: 24px; padding-top: 18px; border-top: 1px solid #e2e8f0; }
                    .signature-box { flex: 1; text-align: center; padding-top: 8px; }
                    .signature-box .sig-title { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #64748b; margin-bottom: 4px; }
                    .signature-box .sig-hint { font-size: 8px; font-style: italic; color: #94a3b8; margin-bottom: 20px; }
                    .signature-box .sig-area { height: 60px; border-bottom: 1.5px solid #cbd5e1; margin-bottom: 6px; }
                    .signature-box .sig-area.stamp { border-bottom: 1.5px dashed #cbd5e1; }

                    .legal-footer { margin-top: 22px; padding-top: 14px; border-top: 2px solid #1a3c5e; display: flex; justify-content: space-between; align-items: center; font-size: 8px; color: #94a3b8; letter-spacing: 0.5px; }
                    .legal-footer .legal-left { display: flex; gap: 16px; flex-wrap: wrap; }
                    .legal-footer .legal-right { text-align: right; font-weight: 600; color: #64748b; }
                    .legal-footer strong { color: #1a3c5e; }

                    .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-35deg); font-size: 100px; font-weight: 900; letter-spacing: 8px; opacity: 0.05; pointer-events: none; z-index: 0; white-space: nowrap; text-transform: uppercase; }
                    .watermark.delivered { color: #10b981; }
                    .watermark.cancelled { color: #ef4444; }
                    .watermark.prepared  { color: #3b82f6; }

                    @media print { body { background: white; padding: 0; } .watermark { display: block; } }
                </style>
            </head>
            <body>
                ${bl.statut === 'livre' ? '<div class="watermark delivered">LIVRÉ</div>' : ''}
                ${bl.statut === 'annule' ? '<div class="watermark cancelled">ANNULÉ</div>' : ''}
                ${bl.statut === 'prepare' ? '<div class="watermark prepared">PRÉPARÉ</div>' : ''}

                <div class="invoice">
                    <div class="top-band">
                        <div class="top-band-left">
                            <div class="logo-container">
                                ${logoSrc ? `<img src="${logoSrc}" alt="Logo" />` : `<div class="logo-fallback">${logoText}</div>`}
                            </div>
                            <div class="company-header">
                                <h1>${company.name}</h1>
                                <div class="tagline">Bon de livraison</div>
                            </div>
                        </div>
                        <div class="top-band-right">
                            <div class="doc-type">Bon de livraison</div>
                            <div class="doc-number">N° ${bl.numero_bl || ''}</div>
                            <div class="doc-date">Émis le ${formatDate(bl.date_livraison)}</div>
                        </div>
                    </div>

                    <div class="status-strip status-${statut.class}">${statut.label}</div>

                    <div class="parties">
                        <div class="party">
                            <div class="party-label">Émetteur</div>
                            <div class="party-name">${company.name}</div>
                            <div class="party-details">
                                <div><span class="label">Adresse</span>${company.address}</div>
                                <div><span class="label">Téléphone</span>${company.phone}</div>
                                <div><span class="label">Email</span>${company.email}</div>
                                <div><span class="label">Site web</span>${company.site_web}</div>
                            </div>
                        </div>
                        <div class="party party-client">
                            <div class="party-label">Client / Destinataire</div>
                            <div class="party-name">${bl.client_nom || 'Client non spécifié'}</div>
                            <div class="party-details">
                                ${bl.code_client ? `<div><span class="label">Code</span>${bl.code_client}</div>` : ''}
                                ${bl.client_telephone ? `<div><span class="label">Téléphone</span>${bl.client_telephone}</div>` : ''}
                                ${bl.client_email ? `<div><span class="label">Email</span>${bl.client_email}</div>` : ''}
                                <div><span class="label">Livraison</span>${bl.adresse_livraison || bl.client_adresse || 'Non spécifiée'}</div>
                                ${bl.destinataire ? `<div><span class="label">Destinataire</span>${bl.destinataire}</div>` : ''}
                            </div>
                        </div>
                    </div>

                    <div class="invoice-meta">
                        <div class="meta-card">
                            <div class="meta-label">Date de livraison</div>
                            <div class="meta-value">${formatDate(bl.date_livraison)}</div>
                        </div>
                        ${bl.numero_facture ? `
                            <div class="meta-card highlight">
                                <div class="meta-label">Facture associée</div>
                                <div class="meta-value">${bl.numero_facture}</div>
                            </div>
                        ` : ''}
                    </div>

                    ${bl.notes ? `<div class="notes-box"><strong>Note :</strong> ${bl.notes}</div>` : ''}

                    <div class="table-wrapper">
                        <table class="lines">
                            <thead>
                                <tr>
                                    <th style="width: 15%;">Référence</th>
                                    <th style="width: 45%;">Désignation</th>
                                    <th class="center" style="width: 10%;">Qté</th>
                                    <th style="width: 15%;">Lot</th>
                                    <th style="width: 15%;">Péremption</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${bl?.lignes && bl.lignes.length > 0
                                    ? bl.lignes.map(ligne => `
                                        <tr>
                                            <td class="ref">${ligne.reference || '-'}</td>
                                            <td class="designation">${ligne.designation || ''}</td>
                                            <td class="quantity">${ligne.quantite || 0}</td>
                                            <td>${ligne.lot || '—'}</td>
                                            <td>${ligne.date_peremption ? formatDate(ligne.date_peremption) : '—'}</td>
                                        </tr>
                                    `).join('')
                                    : '<tr><td colspan="5" style="text-align:center; padding:20px; color:#94a3b8;">Aucun produit dans ce bon de livraison</td></tr>'
                                }
                            </tbody>
                        </table>
                    </div>

                    <div class="totals-section">
                        <div class="totals-box">
                            <div class="row"><span class="lbl">Nombre de références</span><span class="val">${bl?.lignes?.length || 0}</span></div>
                            <div class="row total"><span class="lbl">Quantité totale</span><span class="val">${totalProduits}</span></div>
                        </div>
                    </div>

                    <div class="signatures">
                        <div class="signature-box">
                            <div class="sig-title">Signature Transporteur</div>
                            <div class="sig-hint">Cachet et signature du livreur</div>
                            <div class="sig-area"></div>
                        </div>
                        <div class="signature-box">
                            <div class="sig-title">Signature Client</div>
                            <div class="sig-hint">Précédée de la mention « Reçu conforme »</div>
                            <div class="sig-area stamp"></div>
                        </div>
                    </div>

                    <div class="legal-footer">
                        <div class="legal-left">
                            ${company.rc ? `<span><strong>RC</strong> ${company.rc}</span>` : ''}
                            ${company.nif ? `<span><strong>NIF</strong> ${company.nif}</span>` : ''}
                            ${company.patente ? `<span><strong>Patente</strong> ${company.patente}</span>` : ''}
                        </div>
                        <div class="legal-right">
                            Document généré le ${new Date().toLocaleDateString('fr-FR')} — ${company.name}
                        </div>
                    </div>
                </div>
            </body>
            </html>
        `;
    };

    const handlePrint = async (bl) => {
        try {
            let blData = bl;
            if (!bl.lignes || bl.lignes.length === 0) {
                const details = await fetchBonLivraisonDetails(bl.id_bon_livraison);
                if (details) blData = details;
            }
            const htmlContent = genererHTMLImpression(blData);
            const iframe = document.createElement('iframe');
            iframe.style.position = 'absolute';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = 'none';
            document.body.appendChild(iframe);
            const iframeDoc = iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(htmlContent);
            iframeDoc.close();
            iframe.contentWindow.onload = () => {
                iframe.contentWindow.print();
                setTimeout(() => {
                    if (document.body.contains(iframe)) document.body.removeChild(iframe);
                }, 1000);
            };
            showInfo("Document envoyé à l'impression");
        } catch (err) {
            console.error("Erreur impression:", err);
            showError("Impossible de générer le bon de livraison");
        }
    };

    const ActionButton = ({ icon, onClick, tooltip, disabled = false, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="action-btn-charte"
                onClick={onClick}
                disabled={disabled}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    /* ============================================================
       LOADING / ERROR
       ============================================================ */
    if (loading && !bonsLivraison.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-truck-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des bons de livraison
                        </h5>
                        <p className="text-muted small">Récupération des informations en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error && !bonsLivraison.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={fetchBonsLivraison} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-truck-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Gestion des bons de livraison</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-file-list-line"></i>
                                        {filteredBons.length} BL(s)
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-filter-line"></i>
                                        {filtreStatut === 'tous' ? 'Tous les statuts' : filtreStatut}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchBonsLivraison} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => navigate('/ventes/bl/nouveau')}>
                                <i className="ri-add-line"></i> Nouveau BL
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-primary">
                                    <div className="kpi-icon-charte"><i className="ri-truck-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">BL (mois)</span>
                                        <h3 className="kpi-value-charte">{stats.mois?.nb_bl || 0}</h3>
                                        <span className="kpi-unit-charte">{stats.mois?.nb_produits || 0} produits</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-warning">
                                    <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">À livrer</span>
                                        <h3 className="kpi-value-charte">{stats.statuts?.find(s => s.statut === 'prepare')?.nb || 0}</h3>
                                        <span className="kpi-unit-charte">Préparés</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-success">
                                    <div className="kpi-icon-charte"><i className="ri-check-double-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Livrés</span>
                                        <h3 className="kpi-value-charte">{stats.statuts?.find(s => s.statut === 'livre')?.nb || 0}</h3>
                                        <span className="kpi-unit-charte">Terminés</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-info">
                                    <div className="kpi-icon-charte"><i className="ri-calendar-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Aujourd'hui</span>
                                        <h3 className="kpi-value-charte">{stats.aujourdhui?.nb || 0}</h3>
                                        <span className="kpi-unit-charte">À livrer</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="5">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher BL, client, destinataire..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={filtreStatut}
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="tous">Tous les statuts</option>
                                        <option value="brouillon">Brouillons</option>
                                        <option value="prepare">Préparés</option>
                                        <option value="livre">Livrés</option>
                                        <option value="annule">Annulés</option>
                                    </Form.Select>
                                </Col>
                                <Col md="4">
                                    <div className="text-end">
                                        <span className="count-badge-charte">
                                            <i className="ri-stack-line"></i>
                                            {filteredBons.length} BL(s) trouvé(s)
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-file-list-3-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des bons de livraison</span>
                                    <span className="badge-header-light">{filteredBons.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">N° BL</th>
                                            <SortableHeader field="date_livraison">Date livraison</SortableHeader>
                                            <th>Client</th>
                                            <th>Destinataire</th>
                                            <th>Facture</th>
                                            <th className="text-center">Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <Spinner animation="border" style={{ color: COLORS.primary }} />
                                                </td>
                                            </tr>
                                        ) : currentItems.length > 0 ? (
                                            currentItems.map((bl) => {
                                                const retard = estEnRetard(bl.date_livraison) && bl.statut !== 'livre' && bl.statut !== 'annule';
                                                return (
                                                    <tr key={bl.id_bon_livraison} className={retard ? 'row-late-charte' : ''}>
                                                        <td className="ps-4">
                                                            <Link to={`/ventes/blDetails/${bl.id_bon_livraison}`} className="table-link-charte">
                                                                {bl.numero_bl}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <span className={retard ? 'text-danger fw-semibold' : 'text-muted'}>
                                                                {formatDate(bl.date_livraison)}
                                                            </span>
                                                            {retard && (
                                                                <span className="badge-late-charte">
                                                                    <i className="ri-time-line"></i>Retard
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{bl.client_nom}</div>
                                                            <small className="text-muted">{bl.code_client}</small>
                                                        </td>
                                                        <td className="text-muted">{bl.destinataire || '—'}</td>
                                                        <td>
                                                            {bl.numero_facture ? (
                                                                <Link to={`/ventes/factures/${bl.id_facture}`} className="table-link-charte" style={{ fontSize: '0.8rem' }}>
                                                                    {bl.numero_facture}
                                                                </Link>
                                                            ) : (
                                                                <span className="text-muted">—</span>
                                                            )}
                                                        </td>
                                                        <td className="text-center">{getStatutBadge(bl.statut)}</td>
                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <ActionButton
                                                                    icon="ri-eye-line"
                                                                    onClick={() => navigate(`/ventes/blDetails/${bl.id_bon_livraison}`)}
                                                                    tooltip="Voir détails"
                                                                />
                                                                {bl.statut === 'brouillon' && (
                                                                    <>
                                                                        <ActionButton
                                                                            icon="ri-pencil-line"
                                                                            onClick={() => navigate(`/ventes/bl/modifier/${bl.id_bon_livraison}`)}
                                                                            tooltip="Modifier"
                                                                            color={COLORS.info}
                                                                        />
                                                                        <ActionButton
                                                                            icon="ri-stack-line"
                                                                            onClick={() => handlePreparer(bl.id_bon_livraison)}
                                                                            tooltip="Préparer"
                                                                            color={COLORS.primary}
                                                                        />
                                                                    </>
                                                                )}
                                                                {bl.statut !== 'livre' && bl.statut !== 'annule' && (
                                                                    <ActionButton
                                                                        icon="ri-close-circle-line"
                                                                        onClick={() => handleAnnuler(bl.id_bon_livraison)}
                                                                        tooltip="Annuler"
                                                                        color={COLORS.danger}
                                                                    />
                                                                )}
                                                                <ActionButton
                                                                    icon="ri-printer-line"
                                                                    onClick={() => handlePrint(bl)}
                                                                    tooltip="Imprimer"
                                                                    color={COLORS.secondary}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-inbox-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucun bon de livraison trouvé</h6>
                                                        <p className="text-muted small mb-0">Aucun résultat pour votre recherche</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination-wrapper-charte">
                                    <div className="pagination-info-charte">
                                        Affichage de <strong>{indexOfFirstItem + 1}</strong> à <strong>{Math.min(indexOfLastItem, filteredBons.length)}</strong> sur <strong>{filteredBons.length}</strong> BL(s)
                                    </div>
                                    <Pagination className="pagination-charte mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                <Footer />
            </div>

            {/* ===== MODAL DE CONFIRMATION ===== */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered className="modal-charte">
                <Modal.Header
                    closeButton
                    className="modal-header-charte"
                    style={{
                        background: confirmVariant === 'success'
                            ? `linear-gradient(135deg, ${COLORS.success}, #059669)`
                            : confirmVariant === 'danger'
                            ? `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`
                            : confirmVariant === 'warning'
                            ? `linear-gradient(135deg, ${COLORS.warning}, #d97706)`
                            : `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`
                    }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon">
                            <i className={
                                confirmVariant === 'success' ? 'ri-check-double-line' :
                                confirmVariant === 'danger' ? 'ri-delete-bin-line' :
                                confirmVariant === 'warning' ? 'ri-alert-line' :
                                'ri-information-line'
                            }></i>
                        </div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">{confirmTitle}</Modal.Title>
                            <p className="text-white-50 mb-0 small">Confirmation requise</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4 text-center">
                    <p className="text-muted mb-4" dangerouslySetInnerHTML={{ __html: confirmMessage }}></p>
                    {confirmVariant === 'success' && (
                        <Alert className="alert-info-charte text-start mb-0">
                            <div className="d-flex align-items-start gap-3">
                                <i className="ri-information-line fs-4"></i>
                                <div>
                                    <small className="d-block">✓ Marquer le bon comme livré</small>
                                    <small className="d-block">✓ Enregistrer la date de livraison</small>
                                </div>
                            </div>
                        </Alert>
                    )}
                    {confirmVariant === 'danger' && (
                        <Alert className="alert-error-charte mb-0">
                            <div className="d-flex align-items-center">
                                <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                                <div className="ms-3">
                                    <strong>Attention</strong>
                                    <p className="mb-0 small">Cette action est irréversible</p>
                                </div>
                            </div>
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowConfirmModal(false)} className="btn-modern-outline" disabled={isProcessing}>
                        Annuler
                    </Button>
                    <Button
                        onClick={() => confirmAction && confirmId && confirmAction(confirmId)}
                        disabled={isProcessing}
                        className={
                            confirmVariant === 'success' ? 'btn-modern-success' :
                            confirmVariant === 'danger' ? 'btn-modern-danger' :
                            confirmVariant === 'warning' ? 'btn-modern-warning' :
                            'btn-modern-primary'
                        }
                    >
                        {isProcessing ? (
                            <><Spinner size="sm" className="me-2" />Traitement...</>
                        ) : (
                            <><i className="ri-check-line me-2"></i>Confirmer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus {
                    box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important;
                }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus {
                    box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important;
                }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tbody tr.row-late-charte {
                    background: #fef2f2;
                }
                .table-charte tbody tr.row-late-charte:hover {
                    background: #fee2e2;
                }
                .sortable-th-charte {
                    cursor: pointer; user-select: none;
                    transition: color 0.2s;
                }
                .sortable-th-charte:hover { color: var(--primary); }
                .table-link-charte {
                    color: var(--primary); text-decoration: none;
                    font-weight: 600;
                    transition: color 0.2s;
                }
                .table-link-charte:hover {
                    color: var(--primary-dark);
                    text-decoration: underline;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.35rem 0.9rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                }
                .badge-late-charte {
                    display: inline-flex; align-items: center; gap: 3px;
                    background: var(--danger); color: white;
                    padding: 0.15rem 0.5rem; border-radius: 50px;
                    font-size: 0.65rem; font-weight: 600;
                    margin-left: 0.5rem;
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte {
                    font-size: 0.8rem; color: #64748b;
                }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover:not(:disabled) {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }
                .action-btn-charte:disabled {
                    opacity: 0.5; cursor: not-allowed;
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary, .btn-modern-success,
                .btn-modern-danger, .btn-modern-warning {
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4); color: white;
                }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }
                .alert-info-charte {
                    border: none; background: #eff6ff;
                    border-left: 4px solid var(--info);
                    border-radius: 12px; padding: 1rem 1.25rem;
                    font-size: 0.85rem; color: #1e40af;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}